import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { Euro, ExternalLink } from "lucide-react";
import {
  buildContractPath,
  resolveEconomicMode,
  resolveContractLabel,
  type InstallationIdentityLike,
} from "@/lib/installationContext";

// PR A §4.3 — Economic summary for an installation detail.
//
// - If installation.contractId exists → economics are owned by the contract.
//   We NEVER show installationPrice/0€ as the installation economic figure;
//   instead we surface the contract-managed message + global amount + link.
// - If no contractId → legacy per-installation summary with an explicit
//   "Facturación legacy por instalación" label. 0€ / missing means "no
//   configurado", not a real 0€ contract amount.

function money(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("es-ES", { style: "currency", currency });
}

export function InstallationEconomicSummary({ installation }: { installation: InstallationIdentityLike }) {
  const [, setLocation] = useLocation();
  const mode = resolveEconomicMode(installation);
  const contractRef = resolveContractLabel(installation);

  const economyQuery = trpc.customers.contracts.economyByInstallation.useQuery(
    { installationId: installation.id },
    { enabled: mode === "contract" },
  );

  if (mode === "contract") {
    const data = economyQuery.data as
      | {
          driver: string;
          contractId: number | null;
          customerPartnerId: number | null;
          contractCode: string | null;
          contractTitle: string | null;
          milestoneCount: number;
          effectiveAmount: string | null;
          milestones: unknown[];
          adjustments: unknown[];
        }
      | undefined;

    const milestoneCount = data?.milestoneCount ?? (Array.isArray(data?.milestones) ? data.milestones.length : 0);

    return (
      <Card className="border-green-200 bg-green-50/40">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-green-700 text-base">
            <Euro className="w-4 h-4" />
            Economía gestionada en el contrato {data?.contractCode ?? contractRef ?? ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="font-mono">
              {data?.contractCode ?? contractRef ?? (data?.contractId != null ? `CTR-${String(data.contractId).padStart(6, '0')}` : "Contrato vinculado")}
            </Badge>
            <span className="text-muted-foreground">
              Esta instalación forma parte de un contrato: su facturación se gestiona a nivel de contrato, no por instalación.
            </span>
          </div>
          {economyQuery.isLoading ? (
            <Skeleton className="h-5 w-40" />
          ) : (
            <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,12rem),1fr))] gap-3 [overflow-wrap:anywhere] [&>div]:min-w-0">
              <div>
                <p className="text-xs text-muted-foreground">Importe global (vigente)</p>
                <p className="text-lg font-semibold text-green-800">
                  {money(data?.effectiveAmount)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Hitos de facturación</p>
                <p className="text-lg font-semibold">{milestoneCount}</p>
              </div>
              <div className="col-span-full flex items-end">
                {data?.contractId ? (
                  <Button
                    variant="outline"
                    className="w-full min-w-0 h-auto min-h-9 whitespace-normal"
                    onClick={() => {
                      const customerId = data?.customerPartnerId ?? (installation as any).contractCustomerPartnerId;
                      if (customerId != null && data?.contractId != null) {
                        setLocation(buildContractPath({ customerPartnerId: customerId, contractId: data.contractId, tab: 'economia' }));
                      }
                    }}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Abrir economía del contrato
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Legacy installation (no contract)
  const price = installation.installationPrice;
  const hasPrice =
    price !== null &&
    price !== undefined &&
    price !== "" &&
    Number.isFinite(Number(price)) &&
    Number(price) > 0;

  return (
    <Card className="border">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Euro className="w-4 h-4" />
          Facturación legacy por instalación
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {hasPrice ? (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Importe instalación</p>
            <p className="text-lg font-semibold">{money(price)}</p>
          </div>
        ) : (
          <p className="text-muted-foreground">
            Sin importe configurado para esta instalación.
          </p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Esta instalación no está vinculada a un contrato; la facturación se gestiona por instalación.
        </p>
      </CardContent>
    </Card>
  );
}
